import { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useApi, money } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spinner, ErrorBox, PageHead } from "../components/common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const FAQ = [
  ["Which payment methods can I use?", "Card or PayPal. While checkout is in test mode, use card 4242 4242 4242 4242 with any future expiry and any CVC."],
  ["Can I switch tiers later?", "Yes. Upgrade, downgrade or cancel any time from your dashboard. Access changes immediately."],
  ["What happens if my membership lapses?", "You keep a Free Community account, but members-only library items, events and circles lock automatically."],
  ["How do introductions work?", "Tell the Studio Match Assistant what you need. A Guild staff member reviews every request and makes introductions personally."],
  ["How many seats does Studio & Gallery include?", "Up to 5 seats — you plus four team members, managed from your billing page."],
];
const ROWS = [["Public events & free guides", [1, 1, 1, 1]], ["Directory listing", [1, 1, 1, 1]], ["Full resource library", [0, 1, 1, 1]], ["Members-only events & recordings", [0, 1, 1, 1]], ["Studio circles & forums", [0, 1, 1, 1]], ["Market reports & contracts", [0, 0, 1, 1]], ["Gallery preview invitations", [0, 0, 1, 1]], ["Team seats (up to 5)", [0, 0, 0, 1]], ["List kiln time & studio space", [0, 0, 0, 1]]];

function TierCard({ t, annual, user }) {
  const price = annual ? t.annual_price : t.monthly_price;
  const featured = t.key === "professional";
  const href = t.key === "free" ? (user ? "/dashboard" : "/register?tier=free") : user ? `/checkout?tier=${t.key}&cycle=${annual ? "annual" : "monthly"}` : `/register?tier=${t.key}&cycle=${annual ? "annual" : "monthly"}`;
  return (
    <article className={`kh-card flex flex-col p-6 ${featured ? "border-ochre ring-2 ring-ochre/40" : ""}`} data-testid={`tier-card-${t.key}`}>
      {featured && <span className="chip-ochre mb-3 self-start">Most popular</span>}
      <h3 className="text-2xl">{t.name}</h3>
      <p className="mt-1 text-sm text-[#5a5f69]">{t.tagline}</p>
      <p className="mt-5 font-serif text-4xl text-ink" data-testid={`tier-price-${t.key}`}>{money(price)}<span className="font-sans text-sm text-[#5a5f69]">{t.key === "free" ? "" : annual ? " / year" : " / month"}</span></p>
      <ul className="mt-5 flex-1 space-y-2 text-sm">
        {t.benefits.map((b) => <li key={b} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-celadon-dark" aria-hidden="true" />{b}</li>)}
      </ul>
      <Link to={href} className={`mt-6 ${featured ? "btn-ochre" : "btn-primary"}`} data-testid={`tier-select-${t.key}`}>{t.key === "free" ? "Join free" : `Choose ${t.name}`}</Link>
    </article>
  );
}

export default function Membership() {
  const { data, loading, error, reload } = useApi("/tiers");
  const { user } = useAuth();
  const [annual, setAnnual] = useState(false);
  return (
    <div>
      <PageHead eyebrow="Membership" title="Choose how you'd like to belong" intro="Every tier includes the community. Paid tiers unlock the library, members-only events, studio circles and priority introductions.">
        <div role="group" aria-label="Billing period" className="mt-8 inline-flex rounded-full border border-line bg-white p-1">
          <button onClick={() => setAnnual(false)} aria-pressed={!annual} className={`rounded-full px-5 py-2 text-sm font-semibold ${!annual ? "bg-ink text-paper" : "text-ink"}`} data-testid="membership-toggle-monthly">Monthly</button>
          <button onClick={() => setAnnual(true)} aria-pressed={annual} className={`rounded-full px-5 py-2 text-sm font-semibold ${annual ? "bg-ink text-paper" : "text-ink"}`} data-testid="membership-toggle-annual">Annual <span className="text-xs text-ochre-dark">{annual ? "" : "save ~17%"}</span></button>
        </div>
      </PageHead>
      <section className="kh-container">
        {loading ? <Spinner /> : error ? <ErrorBox message={error} onRetry={reload} /> : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">{data.map((t) => <TierCard key={t.key} t={t} annual={annual} user={user} />)}</div>
            <div className="mt-16 overflow-x-auto rounded-2xl border border-line bg-white">
              <table className="w-full min-w-[640px] text-left text-sm" data-testid="tier-comparison-table">
                <caption className="sr-only">Membership benefit comparison</caption>
                <thead className="bg-sand/60"><tr><th scope="col" className="p-4 font-semibold">Benefit</th>{data.map((t) => <th key={t.key} scope="col" className="p-4 font-serif text-base font-normal text-ink">{t.name}</th>)}</tr></thead>
                <tbody>
                  {ROWS.map(([label, v]) => (
                    <tr key={label} className="border-t border-line"><th scope="row" className="p-4 font-medium">{label}</th>
                      {v.map((x, i) => <td key={i} className="p-4">{x ? <Check className="h-5 w-5 text-celadon-dark" aria-label="Included" /> : <span className="text-[#8a8f99]" aria-label="Not included">—</span>}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
      <section className="kh-container mt-16 max-w-3xl" aria-labelledby="faq-h">
        <h2 id="faq-h" className="mb-4 text-3xl">Frequently asked</h2>
        <Accordion type="single" collapsible className="rounded-2xl border border-line bg-white px-5">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`}><AccordionTrigger className="text-left text-base" data-testid={`faq-item-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-[#4a4f59]">{a}</AccordionContent></AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
